import { imageTester } from "@utils/image-tester";
import { useEffect, useMemo, useState } from "react";

const LOAD_TIMEOUT_MS = 6_000;

export const useImageFallback = (
  primaryOrChain: string | string[],
  imageFallbackUrl?: string,
  defaultImage?: string
) => {
  const chainKey = Array.isArray(primaryOrChain)
    ? primaryOrChain.join("|")
    : `${primaryOrChain ?? ""}|${imageFallbackUrl ?? ""}`;

  const chain = useMemo(() => {
    const urls = Array.isArray(primaryOrChain)
      ? primaryOrChain
      : [primaryOrChain, imageFallbackUrl];
    return urls.filter((url): url is string => Boolean(url?.trim()));
  }, [chainKey]);

  const [image, setImage] = useState(defaultImage || chain[0] || "");

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const testImageUrl = (url: string) =>
      new Promise<string>((resolve, reject) => {
        timer = setTimeout(() => reject(new Error("Timeout")), LOAD_TIMEOUT_MS);
        imageTester(url).then(
          (value) => {
            clearTimeout(timer);
            resolve(value);
          },
          (reason) => {
            clearTimeout(timer);
            reject(reason);
          }
        );
      });

    const run = async () => {
      for (const url of chain) {
        if (cancelled) return;
        try {
          const response = await testImageUrl(url);
          if (cancelled) return;
          if (response) {
            setImage(response);
            return;
          }
        } catch {
          continue;
        }
      }
      if (!cancelled) {
        setImage(defaultImage || chain[chain.length - 1] || chain[0] || "");
      }
    };

    run();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [chain, defaultImage]);

  return image;
};
